/**
 * The review panels (docs/ux/workspace.md): the doctor's decision panel with
 * the changes drawn inline and one row per suggestion; QA's read-only view
 * of what awaits the doctor; the doctor-change panel with Accept and Ask for
 * clarification (never Reject); and QA's preview of a rewrite with grouping
 * and the "No approval needed" flag.
 */

import { cn } from 'cn';
import { useState, type ReactNode } from 'react';
import type { DiffOp, PlannedSuggestion, Suggestion, WorkspaceDoctorChange } from '@/app/types';
import { StateChip } from '@/components/matter/StateChip';
import { changeSummary, WordDiff } from '@/components/matter/WordDiff';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { pluralise } from '@/lib/format';
import { OfflineWrap, ReasonInput, REASON_MAX } from './lists';

type Span = { start: number; end: number; before: string; after: string; ops: DiffOp[] };

/**
 * The whole text with each change drawn where it sits and a superscript
 * marker; a change inside an earlier group's span is not drawn inline (it is
 * "enclosed") and its row says so.
 */
export function InlineDiff({ text, items }: { text: string; items: Span[] }) {
  const parts: ReactNode[] = [];
  let cursor = 0;
  items.forEach((item, i) => {
    if (item.start < cursor) return;
    if (item.start > cursor) parts.push(<span key={`t${i}`}>{text.slice(cursor, item.start)}</span>);
    parts.push(<WordDiff key={`d${i}`} ops={item.ops} />);
    parts.push(<sup key={`m${i}`} className="ml-0.5 text-xs font-medium text-beam-text-muted" aria-label={`change ${i + 1}`}>[{i + 1}]</sup>);
    cursor = Math.max(cursor, item.end);
  });
  if (cursor < text.length) parts.push(<span key="tail">{text.slice(cursor)}</span>);
  return <p className="whitespace-pre-wrap text-base leading-normal">{parts}</p>;
}

export function enclosedIndices(items: Span[]): Set<number> {
  const out = new Set<number>();
  let cursor = 0;
  items.forEach((item, i) => {
    if (item.start < cursor) out.add(i);
    cursor = Math.max(cursor, item.end);
  });
  return out;
}

function Marker({ n }: { n: number }) {
  return <span className="tabular w-8 shrink-0 text-[13px] font-medium text-beam-text-muted">[{n}]</span>;
}

export function DecisionPanel({ boxLabel, text, suggestions, offline, busyId, onDecide, phone }: {
  boxLabel: string;
  text: string;
  suggestions: Suggestion[];
  offline: boolean;
  busyId: string | null;
  onDecide: (id: string, decision: 'accept' | 'reject', reason: string) => void;
  phone?: boolean;
}) {
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const enclosed = enclosedIndices(suggestions);
  return (
    <section aria-label={`QA suggestions for ${boxLabel}`} data-slot="decision-panel" className="flex flex-col gap-3 rounded-xl border border-beam-line bg-beam-paper p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="eyebrow text-beam-text-soft">QA suggestions</h3>
        <span className="text-[13px] text-beam-text-muted">{suggestions.length} open. Decide each, or use the section's Accept all or Reject all.</span>
      </div>
      <InlineDiff text={text} items={suggestions} />
      <ul className="flex flex-col divide-y divide-beam-line border-t border-beam-line">
        {suggestions.map((s, i) => {
          const reason = reasons[s.id] ?? '';
          const busy = busyId === s.id;
          return (
            <li key={s.id} className={cn('flex flex-col gap-2 py-3', enclosed.has(i) && 'border-l-[3px] border-l-beam-warning pl-3')}>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <Marker n={i + 1} />
                <span className="text-[15px]"><WordDiff ops={s.ops} /></span>
                <span className="ml-auto text-[13px] text-beam-text-muted">{s.author}, round {s.round}</span>
              </div>
              {enclosed.has(i) ? <p className="text-[13px] text-beam-warning">Inside an earlier change, which supersedes it if accepted.</p> : null}
              <p className="text-[13px] text-beam-text-soft">{s.reason ? `QA: ${s.reason}` : <em className="text-beam-text-muted">no reason given</em>}</p>
              <div className={cn('flex gap-2', phone ? 'flex-col' : 'items-start')}>
                <ReasonInput id={`decline-${s.id}`} label={`Reason for declining change ${i + 1} (optional)`} placeholder="Reason for declining (optional)" value={reason} onChange={(v) => setReasons((r) => ({ ...r, [s.id]: v }))} phone={phone} />
                <div className={cn('flex gap-2', phone && 'w-full')}>
                  <OfflineWrap offline={offline}><Button className={phone ? 'h-11 flex-1' : 'h-8'} disabled={offline || busyId !== null} aria-label={`Accept change ${i + 1}: ${changeSummary(s.before, s.after)}`} onClick={() => onDecide(s.id, 'accept', '')}>{busy ? 'Deciding' : 'Accept'}</Button></OfflineWrap>
                  <OfflineWrap offline={offline}><Button variant="outline" className={phone ? 'h-11 flex-1' : 'h-8'} disabled={offline || busyId !== null || reason.length > REASON_MAX} aria-label={`Reject change ${i + 1}`} onClick={() => onDecide(s.id, 'reject', reason.trim())}>Reject</Button></OfflineWrap>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function AwaitingPanel({ text, suggestions }: { text: string; suggestions: Suggestion[] }) {
  if (suggestions.length === 0) return null;
  return (
    <section aria-label="Proposals waiting on the doctor" data-slot="awaiting-panel" className="flex flex-col gap-3 rounded-xl border border-beam-line border-l-2 border-l-beam-signature bg-beam-paper p-4">
      <h3 className="text-[13px] font-medium text-beam-text-soft">{suggestions.length === 1 ? 'One proposal waiting on the doctor.' : `${suggestions.length} proposals waiting on the doctor.`}</h3>
      <InlineDiff text={text} items={suggestions} />
      <ul className="flex flex-col divide-y divide-beam-line border-t border-beam-line">
        {suggestions.map((s, i) => (
          <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
            <Marker n={i + 1} />
            <span className="text-[15px]"><WordDiff ops={s.ops} /></span>
            <span className="text-[13px] text-beam-text-muted">{s.author}, round {s.round}</span>
            <span className="text-[13px] text-beam-text-soft">{s.reason || <em className="text-beam-text-muted">no reason given</em>}</span>
            <StateChip label="Waiting on the doctor" tone="neutral" size="sm" className="ml-auto" />
          </li>
        ))}
      </ul>
    </section>
  );
}

export function DoctorChangePanel({ change, offline, busy, onAccept, onAsk, phone }: { change: WorkspaceDoctorChange; offline: boolean; busy: boolean; onAccept: () => void; onAsk: (question: string) => void; phone?: boolean }) {
  const [question, setQuestion] = useState('');
  const [blank, setBlank] = useState(false);
  const ask = () => { const q = question.trim(); if (!q) { setBlank(true); return; } onAsk(q); setQuestion(''); };
  return (
    <section aria-label="Changed by the doctor since your last look" data-slot="doctor-change-panel" className="flex flex-col gap-3 rounded-xl border border-beam-line bg-beam-wash-harbour p-4">
      <h3 className="eyebrow text-beam-harbour-ink">Changed by the doctor since your last look</h3>
      <p className="whitespace-pre-wrap text-base leading-normal"><WordDiff ops={change.ops} /></p>
      <div className={cn('flex gap-2', phone ? 'flex-col' : 'items-start')}>
        <ReasonInput id={`clarify-${change.boxId}`} label="Ask the doctor a question about this change" placeholder="Ask the doctor a question about this change" value={question} onChange={(v) => { setQuestion(v); setBlank(false); }} onEnter={ask} phone={phone} />
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <OfflineWrap offline={offline}><Button className={phone ? 'h-11' : 'h-8'} disabled={offline || busy} onClick={onAccept}>{busy ? 'Accepting' : 'Accept'}</Button></OfflineWrap>
            <OfflineWrap offline={offline}><Button variant="outline" className={phone ? 'h-11' : 'h-8'} disabled={offline || busy || question.length > REASON_MAX} onClick={ask}>Ask for clarification</Button></OfflineWrap>
          </div>
          {blank ? <span role="status" className="text-xs text-beam-error">Write the question first</span> : null}
        </div>
      </div>
    </section>
  );
}

export type PreviewGroup = PlannedSuggestion;

export function PreviewPanel({ boxLabel, current, groups, pending, offline, busy, onReason, onSkip, onCombine, onSeparate, onRegroup, onDiscard, onPropose }: {
  boxLabel: string;
  current: string;
  groups: PreviewGroup[];
  pending: boolean;
  offline: boolean;
  busy: boolean;
  onReason: (index: number, reason: string) => void;
  onSkip: (index: number, skip: boolean) => void;
  onCombine: (indices: number[]) => void;
  onSeparate: (index: number) => void;
  onRegroup: () => void;
  onDiscard: () => void;
  onPropose: () => void;
}) {
  const [picked, setPicked] = useState<number[]>([]);
  const skips = groups.filter((g) => g.skipReview).length;
  const enclosed = enclosedIndices(groups);
  const head = pending ? 'Working out what the doctor will see' : `${groups.length === 1 ? 'One change' : `${groups.length} changes`}. This is what the doctor will see.`;
  return (
    <section aria-label={`Preview of your edits to ${boxLabel}`} data-slot="preview-panel" className="flex flex-col gap-3 rounded-xl border border-beam-line border-l-2 border-l-beam-signature bg-beam-paper p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="eyebrow text-beam-text-soft" aria-live="polite">{head}</h3>
        {skips > 0 ? <span className="text-[13px] text-beam-text-muted">{skips} will be applied without approval.</span> : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="h-8" disabled={picked.length < 2 || offline || pending} onClick={() => { onCombine([...picked].sort((a, b) => a - b)); setPicked([]); }}>Combine selected</Button>
        <span className="text-[13px] text-beam-text-muted">Tick two or more changes that are one thought.</span>
      </div>
      <InlineDiff text={current} items={groups} />
      <ul className="flex flex-col divide-y divide-beam-line border-t border-beam-line">
        {groups.map((g, i) => (
          <li key={i} className={cn('flex flex-col gap-2 py-3', enclosed.has(i) && 'border-l-[3px] border-l-beam-warning pl-3')}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Checkbox id={`pick-${boxLabel}-${i}`} aria-label={`Select change ${i + 1} to combine`} checked={picked.includes(i)} onCheckedChange={(c) => setPicked((p) => (c === true ? [...p, i] : p.filter((x) => x !== i)))} />
              <Marker n={i + 1} />
              <span className="text-[15px]"><WordDiff ops={g.ops} /></span>
              {g.members.length > 1 ? <><span className="text-[13px] text-beam-text-muted">{g.members.length} changes as one</span><Button variant="outline" size="sm" className="h-8" disabled={offline || pending} onClick={() => onSeparate(i)}>Separate</Button></> : null}
            </div>
            {enclosed.has(i) ? <p className="text-[13px] text-beam-warning">Inside an earlier change, which supersedes it if accepted.</p> : null}
            <div className="flex flex-wrap items-start gap-3">
              <ReasonInput id={`group-reason-${i}`} label={`Reason for change ${i + 1} (optional)`} placeholder="Reason for this change (optional)" value={g.reason} onChange={(v) => onReason(i, v)} />
              <Label className="flex h-8 items-center gap-2 text-[13px]"><Checkbox checked={g.skipReview} onCheckedChange={(c) => onSkip(i, c === true)} aria-describedby={`skip-help-${i}`} /> No approval needed</Label>
              <span id={`skip-help-${i}`} className="sr-only">Apply this change without the doctor's approval. The doctor still sees it and can revert it.</span>
            </div>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button variant="ghost" className="h-9" disabled={busy} onClick={onDiscard}>Discard my changes</Button>
        <Button variant="outline" className="h-9" disabled={offline || pending || busy} onClick={onRegroup}>Regroup from scratch</Button>
        <OfflineWrap offline={offline}><Button className="h-9" disabled={offline || pending || busy || groups.length === 0 || groups.some((g) => g.reason.length > REASON_MAX)} onClick={onPropose}>{busy ? 'Proposing' : `Propose ${pluralise(groups.length, 'edit')}`}</Button></OfflineWrap>
      </div>
    </section>
  );
}
